import * as React from "react"

interface UseProgressTimerProps {
  duration: number
  interval?: number
  onComplete?: () => void
}

export function useProgressTimer({
  duration,
  interval = 100,
  onComplete,
}: UseProgressTimerProps) {
  const [progress, setProgress] = React.useState(duration)
  const timerRef = React.useRef<number>()
  const stateRef = React.useRef({
    startTime: 0,
    remaining: duration,
    isPaused: false,
  })

  const cleanup = React.useCallback(() => {
    window.clearTimeout(timerRef.current)
  }, [])

  const reset = React.useCallback(() => {
    cleanup();
    setProgress(duration);
    stateRef.current = {
      startTime: 0,
      remaining: duration,
      isPaused: false,
    };
  }, [cleanup, duration]);

  const tick = React.useCallback(() => {
    const state = stateRef.current
    const elapsed = Date.now() - state.startTime
    const remaining = Math.max(0, state.remaining - elapsed)
    setProgress(remaining)
    if (remaining <= 0) {
      cleanup()
      onComplete?.()
    } else {
      const nextInterval = interval - (elapsed % interval)
      timerRef.current = window.setTimeout(tick, nextInterval)
    }
  }, [cleanup, interval, onComplete])

  const start = React.useCallback(() => {
    cleanup()
    stateRef.current = {
      startTime: Date.now(),
      remaining: duration,
      isPaused: false,
    }
    setProgress(duration)
    timerRef.current = window.setTimeout(tick, interval)
  }, [cleanup, duration, interval, tick])

  const pause = React.useCallback(() => {
    const state = stateRef.current
    if (!state.isPaused) {
      cleanup()
      state.remaining = Math.max(
        0,
        state.remaining - (Date.now() - state.startTime)
      )
      state.isPaused = true
    }
  }, [cleanup])

  const resume = React.useCallback(() => {
    const state = stateRef.current
    if (state.isPaused && state.remaining > 0) {
      state.startTime = Date.now()
      state.isPaused = false
      timerRef.current = window.setTimeout(tick, interval)
    }
  }, [interval, tick])

  React.useEffect(() => cleanup, [cleanup])

return { progress, start, pause, resume, reset }
}